import { useGetAccountingPeriodQuery } from '@api/accounting-and-finance/accounting.period.api.rtk';
import DialogMessage from '@components/dialog/dialog-message';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
import useDisclosure from '@hooks/useDisclosure';
import { RefreshOutlined, Search } from '@mui/icons-material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { useAppSelector } from '@store/index';
import { setSelectedAccountingPeriod } from '@store/slices/accounting-and-finance/accg-selected-accounting-period.slice';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getColor } from '../../assets/theme';
import useAppContext from '../../hooks/useAppContext';
import { selectSessionExpired } from '@store/selector/api.selector';

/**
 * AccountingPeriodSelector component
 *
 * This component renders an Autocomplete input field for selecting accounting period.
 * It fetches accounting periods from an API, manages the selection state, and provides
 * functionality to refresh the accounting period list.
 */
const AccountingPeriodSelector = () => {
  const navigate = useNavigate();
  const isSessionExpired = useAppSelector(selectSessionExpired);
  const { startLoading, stopLoading, toast } = useAppContext();
  const { white } = getColor();
  const messageDialog = useDisclosure();
  const propertyID = useAppSelector(
    (state) => state.properties.selectedProperty.value,
  );

  const selectedAccountingPeriod = useAppSelector(
    (state) => state.selectedAccountingPeriod.selectedAccountingPeriod,
  );

  const [value, setValue] = useState<{ label: string; value: string }>({
    label: '',
    value: '',
  });

  const [nextValue, setNextValue] = useState<
    { label: string; value: string } | undefined
  >();

  const [inputValue, setInputValue] = useState('');

  const [accountingPeriod, setAccountingPeriod] = useState<
    { label: string; value: string }[]
  >([]);

  const dispatch = useDispatch();

  const {
    data: accountingPeriodList,
    isLoading,
    refetch,
    isSuccess,
    isError,
    error,
  } = useGetAccountingPeriodQuery(
    { property: propertyID },
    {
      skip: propertyID == 'All Properties',
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ isLoading, isFetching, ...item }) => ({
        isLoading: isLoading || isFetching,
        ...item,
      }),
    },
  );

  useEffect(() => {
    (async () => {
      if (propertyID === 'All Properties') return;

      if (isError) {
        if (
          error?.toString() !== '/validate-token' &&
          !error?.toString()?.includes('/validate-token')
        ) {
          toast({ message: error, severity: 'error', errorLevel: 1 });
        }
        return;
      }

      // Skip
      if (!isSuccess) return;
      if (isSessionExpired) return;

      if (
        (accountingPeriodList && accountingPeriodList.length <= 0) ||
        !accountingPeriodList
      ) {
        return navigate('/', { state: { initialSetup: 'ABK' } });
      }

      // Accounting periods list
      const newData = accountingPeriodList.map((accountingPeriod) => ({
        label: accountingPeriod?.periodName || 'N/A',
        value: accountingPeriod?.accountingPeriodID || 'N/A',
      }));

      setAccountingPeriod(
        (newData.length === 1
          ? newData
          : newData.filter((item) => item.value !== '')
        ).sort((a, b) => a.label.localeCompare(b.label)),
      );

      // Filter to active and not closed
      const filtered = accountingPeriodList.filter(
        (period) => period.isActive && !period.isClosed,
      );
      if (filtered.length === 0) return;

      // Apply selected period
      const first = filtered[0];
      // ✅ Check if selected period is in the list
      const existsInList = accountingPeriodList.some(
        (p) =>
          p.accountingPeriodID === selectedAccountingPeriod?.accountingPeriodID,
      );
      if (
        !selectedAccountingPeriod?.accountingPeriodID ||
        selectedAccountingPeriod.accountingPeriodID === '' ||
        !existsInList
      ) {
        setValue({ label: first.periodName, value: first.accountingPeriodID });
        setInputValue(first.periodName);
        dispatch(
          setSelectedAccountingPeriod({
            periodName: first.periodName,
            accountingPeriodID: first.accountingPeriodID,
          }),
        );
        return;
      }

      setValue({
        label: selectedAccountingPeriod.periodName,
        value: selectedAccountingPeriod.accountingPeriodID,
      });
      setInputValue(selectedAccountingPeriod.periodName);
    })();
  }, [
    isSuccess,
    isError,
    error,
    isSessionExpired,
    selectedAccountingPeriod,
    propertyID,
    accountingPeriodList,
  ]);

  useEffect(() => {
    setInputValue(value?.label || '');
  }, [value]);

  /**
   * Renders the "No options" text with a refresh button
   * @returns {JSX.Element} The NoOptionsText component
   */
  const NoOptionsText = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        justifyContent: 'space-between',
      }}
    >
      <span>No options found.</span>
      <IconButton
        onClick={async () => {
          startLoading();
          await refetch();
          stopLoading();
        }}
      >
        <RefreshOutlined />
      </IconButton>
    </Box>
  );

  const dialogDisclosure = useDisclosure();

  const handleYes = async () => {
    if (!nextValue) {
      dialogDisclosure.onClose();
      toast({
        message: 'No Accounting Period found. Please try again.',
        severity: 'warning',
      });
      return;
    }
    setValue(nextValue);
    dispatch(
      setSelectedAccountingPeriod({
        periodName: nextValue.label,
        accountingPeriodID: nextValue.value,
      }),
    );
    setInputValue(nextValue.label);
    setNextValue(undefined);
    dialogDisclosure.onClose();
    setTimeout(() => {
      messageDialog.onOpen();
    }, 300);
  };

  return (
    <>
      <Autocomplete
        className='accounting-period-selector'
        disableClearable
        loading={isLoading}
        value={value}
        inputValue={inputValue}
        options={accountingPeriod}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(_e: any, newValue) => {
          setNextValue(newValue);
          dialogDisclosure.onOpen();
        }}
        renderInput={(params) => {
          const value: string = params.inputProps.value as string;
          return (
            <TextField
              label='Accounting Period'
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment
                    position='start'
                    sx={{ paddingLeft: '5px', width: '18px' }}
                  >
                    {!value ? (
                      <Search sx={{ color: white }} />
                    ) : (
                      <DateRangeIcon
                        sx={{ fontSize: '22px', color: '#161C248F!important' }}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
              placeholder={isLoading ? 'Loading...' : 'Search'}
              sx={{
                minWidth: '200px',
                bgcolor: white,
              }}
              size='small'
            />
          );
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <Box
              key={key}
              component='li'
              sx={{ gap: '20px' }}
              {...rest}
              onClick={(e) => {
                props.onClick?.(e);
              }}
            >
              {option.label}
            </Box>
          );
        }}
        noOptionsText={<NoOptionsText />}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 8],
                },
              },
            ],
            sx: {
              '& .MuiAutocomplete-listbox': {
                p: 0,
              },
              '& .MuiAutocomplete-noOptions': {
                py: 0,
                pr: 0.5,
              },
            },
          },
        }}
      />

      <ConfirmationDialog
        open={dialogDisclosure.isOpen}
        title='Change Accounting Period'
        desc='You are about to change the accounting period. Do you wish to proceed?'
        color='warning'
        iconColor='warning'
        onConfirm={handleYes}
        onClose={dialogDisclosure.onClose}
        confirmButtonTitle='Yes'
        cancelButtonTitle='No'
      />

      <DialogMessage
        isOpen={messageDialog.isOpen}
        error={null}
        onClose={messageDialog.onClose}
        message={{
          title: 'Success!',
          desc: `The accounting period has been successfully updated.<br><br> The new accounting period is set to <b style="color:#2B3493">${inputValue}</b>.`,
        }}
      />
    </>
  );
};

export default AccountingPeriodSelector;
